import { useState } from "react";
import { CheckCircle2, Plus, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const ENROLLER_ROLES = [
  "Principal",
  "Deputy Principal",
  "Assistant Principal",
  "Learning Support Coordinator",
  "SENCO",
];

const COHORTS = [
  "Cohort 1",
  "Cohort 2",
];

const MAX_PARTICIPANTS = 6;

function blankEnroller() {
  return { name: "", email: "", school: "", role: "" };
}
function blankParticipant() {
  return { name: "", email: "" };
}

export default function InterestForm() {
  const [cohort, setCohort] = useState("");
  const [enroller, setEnroller] = useState(blankEnroller());
  const [participants, setParticipants] = useState([blankParticipant()]);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null);

  const setEnrollerField = (field, value) =>
    setEnroller((prev) => ({ ...prev, [field]: value }));

  const updateParticipant = (idx, field, value) => {
    setParticipants((prev) =>
      prev.map((p, i) => (i === idx ? { ...p, [field]: value } : p))
    );
  };

  const addParticipant = () => {
    if (participants.length >= MAX_PARTICIPANTS) return;
    setParticipants((prev) => [...prev, blankParticipant()]);
  };

  const removeParticipant = (idx) => {
    if (participants.length <= 1) return;
    setParticipants((prev) => prev.filter((_, i) => i !== idx));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setStatus(null);
    try {
      await api.post("/enrol", {
        cohort,
        enroller_name: enroller.name,
        enroller_email: enroller.email,
        enroller_school: enroller.school,
        enroller_role: enroller.role,
        participants,
      });
      const count = participants.length;
      setStatus({
        ok: true,
        msg: `Kia ora ${enroller.name}! ${count} ${count === 1 ? "participant" : "participants"} enrolled for ${cohort}. A confirmation will be emailed to ${enroller.email} shortly.`,
      });
      setCohort("");
      setEnroller(blankEnroller());
      setParticipants([blankParticipant()]);
    } catch (err) {
      setStatus({ ok: false, msg: formatApiError(err.response?.data?.detail) || err.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="register" className="section bg-[var(--lilac)] text-[var(--charcoal)] relative overflow-hidden">
      <div className="absolute inset-0 deco-lines opacity-30 pointer-events-none" />
      <div className="container-pl relative">
        <div className="grid md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-5">
            <div className="overline !text-[var(--lilac-deep)]">Enrol now</div>
            <h2 className="font-display font-bold text-3xl md:text-5xl mt-3 leading-tight text-[var(--charcoal)]">
              Be part of the first cohort.
            </h2>
            <p className="mt-5 text-[var(--slate)] text-base md:text-lg leading-relaxed">
              School leaders — enrol up to six of your teacher aides in one go. Enter your own
              details, then add each participant below.
            </p>

            <ul className="mt-8 space-y-3 text-[var(--charcoal)]">
              {[
                "First credentialled programme for teacher aides in NZ",
                "Hybrid delivery — fits around your school week",
                "Grounded in ELLP, NZC and culturally responsive practice",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3" data-testid={`register-benefit-${t.length}`}>
                  <CheckCircle2 size={20} className="text-[var(--lilac-deep)] mt-0.5 shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-7">
            <form
              onSubmit={submit}
              className="bg-white text-[var(--charcoal)] rounded-2xl p-6 md:p-10 space-y-7"
              data-testid="interest-form"
            >
              {/* Cohort */}
              <div className="field">
                <label htmlFor="cohort">Cohort *</label>
                <select
                  id="cohort"
                  required
                  value={cohort}
                  onChange={(e) => setCohort(e.target.value)}
                  data-testid="enrol-cohort-select"
                >
                  <option value="">Select a cohort…</option>
                  {COHORTS.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>

              {/* Enroller details */}
              <div className="rounded-xl border border-[var(--border)] p-5 space-y-4" data-testid="enroller-section">
                <div>
                  <div className="text-[0.72rem] uppercase tracking-[0.2em] text-[var(--forest)] font-display font-semibold">
                    Your details
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="field">
                    <label htmlFor="enr-name">Your name *</label>
                    <input
                      id="enr-name"
                      required
                      value={enroller.name}
                      onChange={(e) => setEnrollerField("name", e.target.value)}
                      data-testid="enroller-name-input"
                    />
                  </div>
                  <div className="field">
                    <label htmlFor="enr-email">Your email *</label>
                    <input
                      id="enr-email"
                      type="email"
                      required
                      value={enroller.email}
                      onChange={(e) => setEnrollerField("email", e.target.value)}
                      data-testid="enroller-email-input"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="field">
                    <label htmlFor="enr-school">School *</label>
                    <input
                      id="enr-school"
                      required
                      value={enroller.school}
                      onChange={(e) => setEnrollerField("school", e.target.value)}
                      data-testid="enroller-school-input"
                    />
                  </div>
                  <div className="field">
                    <label htmlFor="enr-role">Your role *</label>
                    <select
                      id="enr-role"
                      required
                      value={enroller.role}
                      onChange={(e) => setEnrollerField("role", e.target.value)}
                      data-testid="enroller-role-select"
                    >
                      <option value="">Select your role…</option>
                      {ENROLLER_ROLES.map((r) => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Participants */}
              <div className="space-y-4">
                <div>
                  <div className="text-[0.72rem] uppercase tracking-[0.2em] text-[var(--forest)] font-display font-semibold">
                    Participants · Up to 6
                  </div>
                  <div className="text-xs text-[var(--slate)] mt-1">
                    The teacher aides who will complete the micro-credential.
                  </div>
                </div>

                {participants.map((p, idx) => (
                  <div
                    key={idx}
                    className="rounded-xl border border-[var(--border)] p-5 space-y-4"
                    data-testid={`participant-row-${idx}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="text-[0.72rem] uppercase tracking-[0.2em] text-[var(--slate)] font-display font-semibold">
                        Participant {idx + 1}
                      </div>
                      {participants.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeParticipant(idx)}
                          className="text-red-600 hover:text-red-700 inline-flex items-center gap-1 text-xs font-display font-semibold"
                          data-testid={`remove-participant-${idx}`}
                        >
                          <Trash2 size={14} /> Remove
                        </button>
                      )}
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="field">
                        <label htmlFor={`p-name-${idx}`}>Name *</label>
                        <input
                          id={`p-name-${idx}`}
                          required
                          value={p.name}
                          onChange={(e) => updateParticipant(idx, "name", e.target.value)}
                          data-testid={`participant-name-${idx}`}
                        />
                      </div>
                      <div className="field">
                        <label htmlFor={`p-email-${idx}`}>Email *</label>
                        <input
                          id={`p-email-${idx}`}
                          type="email"
                          required
                          value={p.email}
                          onChange={(e) => updateParticipant(idx, "email", e.target.value)}
                          data-testid={`participant-email-${idx}`}
                        />
                      </div>
                    </div>
                  </div>
                ))}

                {participants.length < MAX_PARTICIPANTS && (
                  <button
                    type="button"
                    onClick={addParticipant}
                    className="btn btn-secondary w-full"
                    data-testid="add-participant-btn"
                  >
                    <Plus size={16} /> Add participant ({participants.length}/{MAX_PARTICIPANTS})
                  </button>
                )}
              </div>

              <button
                type="submit"
                className="btn btn-primary w-full"
                disabled={submitting}
                data-testid="interest-submit-button"
              >
                {submitting ? "Sending..." : "Enrol now"}
              </button>

              {status && (
                <div
                  className={`text-sm rounded-md px-4 py-3 ${
                    status.ok
                      ? "bg-[var(--sage)] text-[var(--forest)]"
                      : "bg-red-50 text-red-700"
                  }`}
                  data-testid="interest-status-message"
                >
                  {status.msg}
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
